import React, { FC, useMemo } from 'react';
import { DatePicker, Form, Input, InputNumber, Select, Switch } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import moment from 'moment';
import {
	getDateOnlyStartOfDayFromBEDateString,
	getNumberSuffix,
} from '../../utils/DataFormatterUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import BudgetVariablePeriodCycleField from './BudgetVariablePeriodCycleField';

const BudgetFirstSteps: FC<any> = ({ step, formData, form, currency }): React.ReactElement => {
	const values = Form.useWatch([], form);
	const months = Form.useWatch('months', form);

	const hidden = useMemo(() => step !== 0, [step]);

	const startDate = nullSafeGet('periodStartDate', values) || moment();

	const showVariablePeriod = useMemo(() => months === -1, [months]);

	const isUpdate = useMemo(() => !!formData.id, [formData.id]);

	const isPeriodBasedFormData = useMemo(
		() => isUpdate && formData.recurring && !formData.months,
		[formData.months, formData.recurring, isUpdate]
	);

	const variablePeriodInitialValue = useMemo(
		() => (isPeriodBasedFormData ? formData.allCycles : []),
		[formData.allCycles, isPeriodBasedFormData]
	);

	return (
		<>
			<Form.Item
				hidden={hidden}
				name="title"
				label="Title"
				rules={[{ required: true, message: 'Please input a title for your budget.' }]}
			>
				<Input />
			</Form.Item>
			<Form.Item hidden={hidden} name="description" label="Description">
				<TextArea />
			</Form.Item>
			<Form.Item
				hidden={hidden}
				name="periodStartDate"
				label="Budget Start Date"
				initialValue={
					formData.startDate ? getDateOnlyStartOfDayFromBEDateString(formData.startDate) : undefined
				}
				required={true}
				rules={[
					{
						message: 'Please input a start date for your budget.',
						validator: (_, value) => {
							if (value) {
								return Promise.resolve();
							} else {
								return Promise.reject();
							}
						},
					},
				]}
			>
				<DatePicker />
			</Form.Item>
			<Form.Item
				hidden={hidden}
				name="recurring"
				label="Recurring"
				initialValue={formData.recurring || false}
				valuePropName={'checked'}
			>
				<Switch />
			</Form.Item>
			{values && nullSafeGet('recurring', values) ? (
				<Form.Item hidden={hidden} name="months" label="Period" required={true}>
					<Select>
						<Select.Option value={1}>
							Monthly - Starts on the {startDate.date()}
							{getNumberSuffix(startDate.date())} day of each month
						</Select.Option>
						<Select.Option value={3}>
							Quarterly - Starts on the {startDate.date()}
							{getNumberSuffix(startDate.date())} day every third month
						</Select.Option>
						<Select.Option value={12}>
							Yearly - Starts on {startDate.format('MMM D')}
							{getNumberSuffix(startDate.date())} each year
						</Select.Option>
						<Select.Option value={-1}>
							Variable Periods - Starts on {startDate.format('MMM D, yyyy')}
						</Select.Option>
					</Select>
				</Form.Item>
			) : null}
			{showVariablePeriod ? (
				<div style={{ display: hidden ? 'none' : 'block' }}>
					<BudgetVariablePeriodCycleField
						initialValue={variablePeriodInitialValue}
						form={form}
						startDate={startDate}
					/>
				</div>
			) : (
				<Form.Item
					hidden={hidden}
					name="periodEndDate"
					label="Budget End Date"
					initialValue={
						formData.endDate ? getDateOnlyStartOfDayFromBEDateString(formData.endDate) : undefined
					}
				>
					<DatePicker />
				</Form.Item>
			)}
			<Form.Item
				hidden={hidden}
				name="avgAccrualAmountPerWorkOrder"
				label="Average Accrual Amount Per Work Order"
			>
				<InputNumber style={{ width: 600 }} addonAfter={currency.id} />
			</Form.Item>
		</>
	);
};

export default BudgetFirstSteps;
